import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, LogOut } from 'lucide-react';
import { LanguageSwitch } from '../components';
import { useAuth } from './AuthContext';

const ROLE_LABELS: Record<string, string> = {
  SuperAdmin: 'Super admin', RegionalManager: 'Regional manager', OperationsSupervisor: 'Operations supervisor', Finance: 'Finance',
  TeamLead: 'Team lead', Washer: 'Washer', PartnerAdmin: 'Partner admin', PartnerViewer: 'Partner viewer',
};

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
}

/** Avatar button in the top bar; opens name / role / language / log out. Keeps the top bar uncluttered on phones. */
export function UserMenu({ onLogout }: { onLogout?: () => void }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  if (!user) return null;
  const role = t(`roles.${user.role}`, { defaultValue: ROLE_LABELS[user.role] ?? user.role });
  return (
    <div className="gr-usermenu" ref={ref}>
      <button type="button" className="gr-usermenu__btn" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}>
        <span className="gr-avatar" aria-hidden>{initials(user.fullName)}</span>
        <span className="gr-usermenu__who">
          <span className="gr-usermenu__name">{user.fullName}</span>
          <span className="gr-usermenu__role">{role}</span>
        </span>
        <ChevronDown size={16} className="gr-usermenu__chev" aria-hidden />
      </button>
      {open && (
        <div className="gr-usermenu__panel" role="menu">
          <div className="gr-usermenu__head">
            <span className="gr-avatar gr-avatar--lg" aria-hidden>{initials(user.fullName)}</span>
            <div style={{ minWidth: 0 }}>
              <div className="gr-usermenu__name">{user.fullName}</div>
              <div className="gr-usermenu__role">{role}</div>
              {user.email && <div className="gr-usermenu__email">{user.email}</div>}
            </div>
          </div>
          <label className="gr-usermenu__row">
            <span>{t('common.language', { defaultValue: 'Language' })}</span>
            <LanguageSwitch />
          </label>
          <button type="button" role="menuitem" className="gr-usermenu__logout" onClick={() => { setOpen(false); (onLogout ?? logout)(); }}>
            <LogOut size={18} aria-hidden /> {t('common.logout')}
          </button>
        </div>
      )}
    </div>
  );
}
